import React, { useEffect } from 'react';
import { ArrowRight, FileText, Lightbulb, Radar } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  GeoNewsIndexArticle,
  GeoNewsCategorySlug,
  geoNewsCategories,
  getGeoNewsArticlePath,
  getGeoNewsArticlesByCategory,
} from '../data/geoNewsIndex';
import OptimizedImage from './OptimizedImage';

const categoryConfig: Record<
  GeoNewsCategorySlug,
  { icon: React.ReactNode; limit: number; label: string }
> = {
  reports: {
    icon: <FileText className="h-5 w-5" />,
    limit: 8,
    label: '行业报告',
  },
  insights: {
    icon: <Lightbulb className="h-5 w-5" />,
    limit: 8,
    label: '深度洞察',
  },
  intelligence: {
    icon: <Radar className="h-5 w-5" />,
    limit: 8,
    label: '算法情报',
  },
};

const ReportItem: React.FC<{ article: GeoNewsIndexArticle }> = ({ article }) => (
  <Link
    to={getGeoNewsArticlePath(article)}
    className="group grid grid-cols-[84px_1fr] gap-3 rounded-xl border border-slate-200 bg-white p-2 transition-all hover:-translate-y-0.5 hover:border-[#D83A40]/35 hover:shadow-[0_14px_35px_rgba(126,23,29,0.10)]"
  >
    <div className="h-16 overflow-hidden rounded-lg bg-[#111319]">
      <OptimizedImage
        src={article.cover}
        alt={article.title}
        className="h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105"
      />
    </div>
    <div className="min-w-0">
      <h3 className="line-clamp-2 text-sm font-bold leading-5 text-slate-900 transition-colors group-hover:text-[#B7262D]">
        {article.title}
      </h3>
      <p className="mt-1 text-[11px] text-slate-400">{article.date.slice(0, 10)}</p>
    </div>
  </Link>
);

const TextItem: React.FC<{ article: GeoNewsIndexArticle; index: number }> = ({
  article,
  index,
}) => (
  <li>
    <Link
      to={getGeoNewsArticlePath(article)}
      className="group grid grid-cols-[28px_1fr] gap-3 border-b border-slate-100 py-3.5 last:border-0"
    >
      <span className="mt-0.5 font-mono text-xs font-bold text-[#C92E35]/70">
        {String(index + 1).padStart(2, '0')}
      </span>
      <span>
        <span className="line-clamp-2 text-sm font-semibold leading-6 text-slate-700 transition-colors group-hover:text-[#B7262D]">
          {article.title}
        </span>
        <span className="mt-1 block text-[11px] text-slate-400">
          {article.date.slice(0, 10)}
        </span>
      </span>
    </Link>
  </li>
);

const GeoNewsHomeSection: React.FC = () => {
  useEffect(() => {
    if (sessionStorage.getItem('geo-news-return-target') !== 'section-top') return;

    const restorePosition = () => {
      const section = document.querySelector<HTMLElement>('[data-news-section]');
      if (!section) return;
      window.scrollTo({
        top: Math.max(0, section.offsetTop - 80),
        behavior: 'auto',
      });
    };

    const frame = requestAnimationFrame(() => requestAnimationFrame(restorePosition));
    const timer = window.setTimeout(() => {
      restorePosition();
      sessionStorage.removeItem('geo-news-return-target');
      window.history.scrollRestoration = 'auto';
    }, 180);

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, []);

  const rememberReturnTarget = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest('a[href^="/knowledge/"]')) {
      window.history.scrollRestoration = 'manual';
      sessionStorage.setItem('geo-news-return-target', 'section-top');
    }
  };

  return (
    <section
      data-news-section
      onClickCapture={rememberReturnTarget}
      className="border-t border-slate-100 bg-[#F6F7F9] py-16"
    >
      <div className="mx-auto max-w-[1400px] px-6">
        <header className="mb-12 text-center">
          <h2 className="text-3xl font-black tracking-tight text-[#111827] md:text-4xl">
            GEO优化行业资讯
          </h2>
          <p className="mt-3 text-base text-slate-500">
            汇集GEO行业报告、深度洞察与算法情报，赋能企业AI营销
          </p>
        </header>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {geoNewsCategories.map((category) => {
            const config = categoryConfig[category.slug];
            const articles = getGeoNewsArticlesByCategory(category.slug).slice(0, config.limit);
            return (
              <article
                key={category.slug}
                className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_12px_35px_rgba(15,23,42,0.05)] lg:h-[872px]"
              >
                <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#D83A40] to-[#A91E26] text-white shadow-[0_8px_20px_rgba(183,38,45,0.20)]">
                      {config.icon}
                    </span>
                    <div>
                      <h3 className="font-black text-slate-900">{category.title}</h3>
                      <p className="text-[11px] text-slate-400">{config.label}</p>
                    </div>
                  </div>
                  <Link
                    to={`/knowledge/${category.slug}`}
                    className="inline-flex items-center gap-1 text-xs font-bold text-[#B7262D] hover:text-[#8C171E]"
                  >
                    更多 <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>

                <div className="p-5">
                  {category.slug === 'reports' ? (
                    <div className="space-y-2">
                      {articles.map((article) => (
                        <ReportItem key={article.id} article={article} />
                      ))}
                    </div>
                  ) : (
                    <ul>
                      {articles.map((article, index) => (
                        <TextItem key={article.id} article={article} index={index} />
                      ))}
                    </ul>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default GeoNewsHomeSection;
